// InstructionsPanel — collapsible "how to use" panel for the Cut To Length
// Optimiser. Ported from the Drillcut hub's ToolInstructions, restyled with
// BSP design tokens.
import { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

export interface InstructionStep {
  text: string;
  sub?: string;
}

export interface InstructionsPanelProps {
  /** Short tool description (1–2 sentences) */
  description: string;
  /** Numbered steps */
  steps: readonly InstructionStep[];
  /** Optional tips shown below the steps */
  tips?: readonly string[];
  /** If true, starts expanded (default: false) */
  defaultOpen?: boolean;
}

export function InstructionsPanel({
  description,
  steps,
  tips,
  defaultOpen = false,
}: InstructionsPanelProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      style={{
        background: "var(--bsp-surface)",
        border: "1px solid var(--bsp-border)",
        borderRadius: 8,
        marginBottom: 16,
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        role="button"
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          cursor: "pointer",
          userSelect: "none",
          borderBottom: open ? "1px solid var(--bsp-border)" : "none",
        }}
      >
        <span
          className="bsp-eyebrow"
          style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }}
        >
          <HelpCircle size={13} strokeWidth={2.5} />
          How to use
        </span>
        {open ? (
          <ChevronUp size={15} color="var(--bsp-fg-subtle)" />
        ) : (
          <ChevronDown size={15} color="var(--bsp-fg-subtle)" />
        )}
      </div>

      {open && (
        <div
          style={{
            padding: "12px 16px",
            fontSize: 13,
            color: "var(--bsp-fg)",
            lineHeight: 1.55,
          }}
        >
          <p style={{ margin: "0 0 14px", color: "var(--bsp-fg-muted)", fontSize: 12.5 }}>
            {description}
          </p>

          <ol
            style={{
              margin: 0,
              paddingLeft: 20,
              listStyleType: "decimal",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {steps.map((step, i) => (
              <li key={i} style={{ fontSize: 12.5 }}>
                <span>{step.text}</span>
                {step.sub && (
                  <span
                    style={{
                      display: "block",
                      fontSize: 11.5,
                      color: "var(--bsp-fg-subtle)",
                      marginTop: 2,
                    }}
                  >
                    {step.sub}
                  </span>
                )}
              </li>
            ))}
          </ol>

          {tips && tips.length > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                background: "var(--bsp-blue-pale)",
                borderRadius: 6,
                borderLeft: "3px solid var(--bsp-blue)",
              }}
            >
              <div className="bsp-eyebrow" style={{ marginBottom: 6, fontSize: 10 }}>
                Tips
              </div>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 16,
                  listStyleType: "disc",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {tips.map((tip, i) => (
                  <li key={i} style={{ fontSize: 11.5, color: "var(--bsp-fg-muted)" }}>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
